import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Switch, FormControlLabel, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import AgentFormContactsData from '../AgentFormContactsData';
import AgentFormDeliveryAddress from '../AgentFormDeliveryAddress';
import AgentFormBillingAddress from '../AgentFormBillingAddress';
import EditAvatar from '../EditAvatar';
import PanelCard from './PanelCard';
import PersonalFields from './fields/PersonalFields';
import Agentv2 from '../../../../types/Agentv2.types';
import { AgentInputType } from '../../../../types/AgentInputType.types';

function PersonalPanel() {
  const theme = useTheme();
  const { values, setFieldValue } = useFormikContext<AgentInputType | Agentv2>();

  const [showBillingAddress, setShowBillingAddress] = useState(false);

  useEffect(() => {
    if (values.address2 || values.zip2 || values.city2 || values.country2_id) {
      setShowBillingAddress(true);
    }
  }, [values.address2, values.zip2, values.city2, values.country2_id]);

  const handleBillingToggle = useCallback(() => {
    const newValue = !showBillingAddress;
    setShowBillingAddress(newValue);
    if (!newValue) {
      // Alle Rechnungsadress-Felder leeren
      setFieldValue('address2', '');
      setFieldValue('zip2', '');
      setFieldValue('city2', '');
      setFieldValue('country2_id', null);
    }
  }, [showBillingAddress, setFieldValue]);

  return (
    <Box>
      <PanelCard title="Persönliche Daten" subtitle="Stammdaten des Beraters">
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Box sx={{ flexShrink: 0, width: 120 }}>
            <EditAvatar currentAgentAvatar={(values as Agentv2).thumbnail} agentID={values.agent_id} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PersonalFields />
          </Box>
        </Box>
      </PanelCard>

      <PanelCard title="Kontaktdaten" subtitle="Telefon, E-Mail, Fax">
        <AgentFormContactsData />
      </PanelCard>

      <PanelCard title="Adressen" subtitle="Liefer- und Rechnungsadresse">
        <Typography
          variant="overline"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            color: theme.palette.text.disabled,
            mb: 1.5,
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'block',
          }}
        >
          Lieferadresse
        </Typography>
        <AgentFormDeliveryAddress />
        <FormControlLabel
          control={<Switch checked={showBillingAddress} onChange={handleBillingToggle} color="primary" />}
          label="Abweichende Rechnungsadresse"
          sx={{
            mt: 2.5,
            mb: 1.5,
            '& .MuiTypography-root': {
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.text.secondary,
            },
          }}
        />
        {showBillingAddress && (
          <Box
            sx={{
              pt: 1,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <AgentFormBillingAddress />
          </Box>
        )}
      </PanelCard>
    </Box>
  );
}

export default PersonalPanel;
